import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, Subject } from 'rxjs';
import { environment } from '@environment';
import { MessageDTO } from '@sb-tools/typescript/models/dto/message.dto';
import { AddTicketModel } from 'src/sb-modules/Frontend-form-field-module/common/models/add-ticket-model.model';
import { Importance } from '@sb-tools/typescript/enums/importance.enum';
import { BaseUserWithRole } from '@sb-tools/typescript/models/base-user-with-role.model';
import { Tag } from '@sb-tools/typescript/models/tag.model';
import { TicketReminderDTO } from '@sb-tools/typescript/models/dto/ticket-reminder.dto';
import { Sentiment } from '@sb-tools/typescript/enums/sentiment.enum';
import { TicketStatus } from '@sb-tools/typescript/enums/ticket-status.enum';
import { DetailedTicketDTO } from '../../../core/models/dto/detailed-ticket.dto';
import { TicketAssigneeUpdateModel } from '../../../core/models/ticket-assignee-update.model';
import { RedactAttachmentsResponseModel } from '../../../core/models/redact-attachments-response.model';
import { ContentVisibilityModel } from '../../../core/models/content-visibility-model.model';
import { HideContentPartResponseDTO } from '../../../core/models/dto/hide-content-part-response.dto';
import { TicketRedactionModel } from '../../../core/models/ticket-redaction.model';
import { UpdateTicketCategoryResult } from '../../../core/models/update-ticket-category-result.model';
import { UpdateTicketTagsResult } from '../../../core/models/update-ticket-tags-result.model';
import { SessionAuthenticationDTO } from '@sb-tools/typescript/models/dto/session-authentication.dto';

@Injectable({
    providedIn: 'root',
})
export class TicketService {
    constructor(private httpClient: HttpClient) {}

    isRedacting: boolean;

    onTicketRedactedSubject = new Subject<{ operationId: string; ticket: DetailedTicketDTO }>();

    addServiceNowCase(ticketId: number): Observable<void> {
        return this.httpClient.post<void>(environment.apiInboxUrl + 'tickets/' + ticketId + '/ServiceNow', null);
    }

    addReminder(ticketId: number, reminder: TicketReminderDTO): Observable<TicketReminderDTO> {
        return this.httpClient.put<TicketReminderDTO>(
            environment.apiInboxUrl + 'tickets/' + ticketId + '/reminder',
            reminder,
        );
    }

    anonymize(ticketId: number): Observable<DetailedTicketDTO> {
        return this.httpClient.patch<DetailedTicketDTO>(
            environment.apiInboxUrl + 'tickets/' + ticketId + '/Anonymize',
            null,
        );
    }

    assign(
        ticketId: number,
        previousAssigneeId: string,
        newAssigneeId?: string,
    ): Observable<TicketAssigneeUpdateModel> {
        return this.httpClient.patch<TicketAssigneeUpdateModel>(
            environment.apiInboxUrl + 'tickets/' + ticketId + '/assign',
            null,
            {
                params: {
                    previousAssigneeId,
                    newAssigneeId,
                },
            },
        );
    }

    changeImportance(ticketId: number, importance: Importance): Observable<void> {
        return this.httpClient.patch<void>(
            environment.apiInboxUrl + 'tickets/' + ticketId + '/Importance?importance=' + importance,
            null,
        );
    }

    changeMuteState(ticketId: number, isMuted: boolean): Observable<void> {
        return this.httpClient.patch<void>(
            environment.apiInboxUrl + 'tickets/' + ticketId + '/Mute?isMuted=' + isMuted,
            null,
        );
    }

    create(ticket: AddTicketModel): Observable<number> {
        return this.httpClient.post<number>(environment.apiInboxUrl + 'tickets/internal', ticket);
    }

    createRequestIdentification(ticketId: number): Observable<void> {
        return this.httpClient.post<void>(
            environment.apiInboxUrl + 'tickets/' + ticketId + '/Author/RequestIdentification',
            null,
        );
    }

    requestSessionAuthentication(ticketId: number): Observable<SessionAuthenticationDTO> {
        return this.httpClient.post<SessionAuthenticationDTO>(
            environment.apiInboxUrl + 'tickets/' + ticketId + '/SessionAuthentication/Request',
            null,
        );
    }

    deletePost(ticketId: number): Observable<void> {
        return this.httpClient.delete<void>(environment.apiInboxUrl + 'tickets/' + ticketId + '/post');
    }

    deleteReminder(ticketId: number, replyId?: number): Observable<void> {
        return this.httpClient.delete<void>(
            environment.apiInboxUrl + 'tickets/' + ticketId + '/reminder' + (replyId ? `?replyId=${replyId}` : ''),
        );
    }

    emitTicketUpdated(data: { operationId: string; ticket: DetailedTicketDTO }): void {
        return this.onTicketRedactedSubject.next(data);
    }

    get(ticketId: number): Observable<DetailedTicketDTO> {
        return this.httpClient.get<DetailedTicketDTO>(environment.apiInboxUrl + 'tickets/' + ticketId);
    }

    getOriginalMessage(ticketId: number): Observable<MessageDTO> {
        return this.httpClient.get<MessageDTO>(environment.apiInboxUrl + 'tickets/' + ticketId + '/originalMessage');
    }

    getRedactAttachments(ticketId: number): Observable<RedactAttachmentsResponseModel> {
        return this.httpClient.get<RedactAttachmentsResponseModel>(
            environment.apiInboxUrl + 'tickets/' + ticketId + '/redact/Attachments',
        );
    }

    giveAccess(ticketId: number, data: { message: string; users: string[] }): Observable<void> {
        return this.httpClient.post<void>(environment.apiInboxUrl + 'tickets/' + ticketId + '/access/', data);
    }

    hidePost(ticketId: number, isVisible: boolean): Observable<void> {
        return this.httpClient.patch<void>(
            environment.apiInboxUrl + 'tickets/' + ticketId + '/visibility/?isVisible=' + isVisible,
            null,
        );
    }

    hideContent(
        ticketId: number,
        contentVisibility: ContentVisibilityModel = null,
    ): Observable<HideContentPartResponseDTO> {
        return this.httpClient.patch<HideContentPartResponseDTO>(
            environment.apiInboxUrl + 'tickets/' + ticketId + '/ContentVisibility',
            contentVisibility,
        );
    }

    pingTeammates(ticketId: number): Observable<void> {
        return this.httpClient.post<void>(environment.apiInboxUrl + 'tickets/' + ticketId + '/BergHansen', null);
    }

    redactTicket(
        ticketId: number,
        model: TicketRedactionModel,
    ): Observable<{ operationId: string; ticket: DetailedTicketDTO }> {
        return this.httpClient.patch<{ operationId: string; ticket: DetailedTicketDTO }>(
            environment.apiInboxUrl + 'tickets/' + ticketId + '/redact',
            model,
        );
    }

    review(ticketId: number, isApproved: boolean): Observable<void> {
        return this.httpClient.patch<void>(
            environment.apiInboxUrl + 'tickets/' + ticketId + '/review?isApproved=' + isApproved,
            null,
        );
    }

    forward(ticket: AddTicketModel): Observable<number> {
        return this.httpClient.post<number>(environment.apiInboxUrl + 'tickets/forward', ticket);
    }

    sendToIndigo(ticketId: number): Observable<void> {
        return this.httpClient.post<void>(environment.apiInboxUrl + 'tickets/' + ticketId + '/Indigo', null);
    }

    sentiment(ticketId: number, sentiment: Sentiment): Observable<void> {
        return this.httpClient.patch<void>(
            environment.apiInboxUrl + 'tickets/' + ticketId + '/sentiment?sentiment=' + sentiment,
            null,
        );
    }

    deleteHiddenContent(ticketId: number, replyId: number = null): Observable<HideContentPartResponseDTO> {
        return this.httpClient.delete<HideContentPartResponseDTO>(
            environment.apiInboxUrl + 'tickets/' + ticketId + '/ContentVisibility',
            { params: { replyId } },
        );
    }

    spam(ticketId: number, isSpam: boolean): Observable<void> {
        return this.httpClient.patch<void>(
            environment.apiInboxUrl + 'tickets/' + ticketId + '/spam?isSpam=' + isSpam,
            null,
        );
    }

    takeOver(ticketId: number, isAssigned: boolean): Observable<{ status: TicketStatus }> {
        return this.httpClient.patch<{ status: TicketStatus }>(
            environment.apiInboxUrl + 'tickets/' + ticketId + '/BotAssignment/?isAssigned=' + isAssigned,
            null,
        );
    }

    updateCategory(
        ticketId: number,
        categoryId: number,
        isClosed = false,
        isHandled = false,
    ): Observable<UpdateTicketCategoryResult> {
        return this.httpClient.patch<UpdateTicketCategoryResult>(
            environment.apiInboxUrl + 'tickets/' + ticketId + '/category/v2',
            null,
            {
                params: {
                    categoryId,
                    isClosed,
                    isHandled,
                },
            },
        );
    }

    updateFirstMessageCreator(
        ticketId: number,
        userInfo: { userId: string; name: string; email: string },
    ): Observable<BaseUserWithRole> {
        return this.httpClient.patch<BaseUserWithRole>(
            environment.apiInboxUrl + 'tickets/' + ticketId + '/firstMessageCreator',
            userInfo,
        );
    }

    updateStatus(
        ticketId: number,
        status: TicketStatus,
        isHandled: boolean,
        isShouldBeRestoredFromSpam?: boolean,
    ): Observable<void> {
        return this.httpClient.patch<void>(
            environment.apiInboxUrl +
                'tickets/' +
                ticketId +
                '/Status?status=' +
                status +
                '&isHandled=' +
                isHandled +
                (isShouldBeRestoredFromSpam ? '&restoreFromSpam=true' : ''),
            null,
        );
    }

    updateTags(ticketId: number, tags: Tag[], isClosed = false, isHandled = false): Observable<UpdateTicketTagsResult> {
        return this.httpClient.put<UpdateTicketTagsResult>(
            environment.apiInboxUrl + 'tickets/' + ticketId + '/tags/v2',
            tags,
            {
                params: {
                    isClosed,
                    isHandled,
                },
            },
        );
    }

    updateTitle(ticketId: number, title: string): Observable<string> {
        return this.httpClient.patch<string>(environment.apiInboxUrl + 'tickets/' + ticketId + '/title', title);
    }
}
